import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEntityList, useOrgPipelines, useOrgProjects } from '../data/entities';
import { MODULES } from '../data/consoleModules';
import { useAuth } from '../AuthContext';
import {
  applyBook, deployStamp, healthStamp, loadBook, markCleared, markRead, reviewStamp, settleBook,
} from './noticeState';
import { openBugReport, openFeatureRequest } from './BugReportDialog';
import { buildIndex, rank, recordPick, highlight } from '../utils/consoleSearch';

// Console tools (decision deck-ia-console, re-homed by ux-single-nav-in-org):
// search over the org's named entities + modules (⌘K) and a notifications
// bell fed by review / deploy / instance-health events. Rendered inside the
// platform top bar's org-mode utility strip (PlatformNav) — there is one bar
// of chrome, and the sidebar is the only thing that moves between modules.
// `askNova` opens the copilot dock from any module.
export function askNova(prompt, { attach = false } = {}) {
  window.dispatchEvent(new CustomEvent('nv:ask-nova', { detail: { prompt, attach } }));
}

export default function ConsoleTools({ orgId }) {
  const [bell, setBell] = useState(false);
  const bellRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setBell(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!bell) return undefined;
    const onDoc = (e) => { if (bellRef.current && !bellRef.current.contains(e.target)) setBell(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [bell]);

  const { events, ready } = useNotifications(orgId);
  const { listed, unread, clearOne, clearAll } = useNoticeBook(orgId, user?.uid, events, bell, ready);

  return (
    <>
      <SearchBox orgId={orgId} uid={user?.uid} />
      <div className="ch-right" ref={bellRef}>
        <button
          type="button"
          className="cs-btn cs-btn-outline cs-btn-sm ch-bell"
          onClick={() => setBell((v) => !v)}
          aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
          aria-expanded={bell}
          aria-haspopup="dialog"
          title="Reviews waiting, deploys, instance health"
        >
          🔔
          {unread > 0 && <span className="ch-bell-count">{unread > 99 ? '99+' : unread}</span>}
        </button>
        {bell && (
          <div className="ch-pop" role="dialog" aria-label="Notifications">
            <div className="ch-pop-bar">
              <span className="ch-pop-title">Notifications</span>
              {listed.length > 0 && (
                <button type="button" className="ch-pop-clear" onClick={clearAll}>Clear all</button>
              )}
            </div>
            <div className="ch-pop-list">
              {!listed.length && <div className="ch-pop-empty">Nothing needs you right now.</div>}
              {listed.map((ev) => (
                <div key={ev.id} className={`ch-pop-row${ev.read ? ' is-read' : ''}`}>
                  <Link to={ev.to} className="ch-pop-link" onClick={() => setBell(false)}>
                    <span className={`ch-pop-dot${ev.read ? ' is-read' : ''}`} aria-hidden="true" />
                    <span className={`mf-chip is-${ev.tone}`}>{ev.kind}</span>
                    <span className="ch-pop-text">{ev.text}</span>
                    {ev.at && <span className="cs-help">{ago(ev.at)}</span>}
                  </Link>
                  <button
                    type="button"
                    className="ch-pop-x"
                    aria-label={`Clear ${ev.kind} notification`}
                    title="Clear this notification"
                    onClick={() => clearOne(ev)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ago(ts) {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

// Review items waiting, deploys in flight / failed (last 24h), instances
// whose monitor is not green. All live; small orgs, few subscriptions.
function useNotifications(orgId) {
  const { docs: reviews } = useEntityList('reviewItem', { org: orgId });
  const { docs: apps } = useEntityList('appInstance', { org: orgId });
  const dayAgo = Date.now() - 86400000;
  const ready = reviews != null && apps != null;
  const events = useMemo(() => {
    const out = [];
    for (const r of reviews || []) {
      if (r.status !== 'pending') continue;
      out.push({
        id: `rev-${r.id}`, stamp: reviewStamp(r), kind: 'review', tone: r.priority === 'urgent' ? 'danger' : 'warn',
        text: `${r.queue || 'general'}${r.priority && r.priority !== 'normal' ? ` · ${r.priority}` : ''}: ${(r.content || r.instructions || '').replace(/\s+/g, ' ').slice(0, 90) || 'item waiting'}`,
        to: `/orgs/${orgId}/human-review?item=${r.id}`, at: r.createdAt,
      });
    }
    for (const a of apps || []) {
      if (a.status === 'live' && a.monitor?.state === 'down') {
        out.push({
          id: `mon-${a.id}`, stamp: healthStamp(a), kind: 'health', tone: 'danger',
          text: `${a.name} is down${a.monitor.consecFails ? ` (${a.monitor.consecFails} failed probes)` : ''}`,
          to: `/orgs/${orgId}/apps/${a.id}`, at: a.monitor.lastCheckAt || a.updatedAt,
        });
      }
      if (a.status === 'provisioning' && (a.updatedAt || a.createdAt) > dayAgo) {
        out.push({
          id: `prov-${a.id}`, stamp: deployStamp(a), kind: 'deploy', tone: 'info',
          text: `${a.name} is provisioning`, to: `/orgs/${orgId}/apps/${a.id}`, at: a.updatedAt || a.createdAt,
        });
      }
    }
    out.sort((x, y) => (y.at || 0) - (x.at || 0));
    return out;
  }, [reviews, apps, orgId, dayAgo]);
  return { events: ready ? events : [], ready };
}

const noticeKey = (uid, orgId) => `nv.notices.${uid || 'local'}.${orgId}`;

function readStore(key) {
  try { return loadBook(JSON.parse(localStorage.getItem(key) || 'null')); } catch { return loadBook(null); }
}

// Badge = unread. Opening the bell marks every visible row read. Clear
// hides a row (or all of them) until that occurrence's stamp changes.
function useNoticeBook(orgId, uid, events, open, ready) {
  const key = noticeKey(uid, orgId);
  const [slot, setSlot] = useState(() => ({ key, book: readStore(key) }));
  // Ids that were unread when this panel opened. They keep a dot until it
  // closes; the badge drops immediately and the book is already marked read.
  const [fresh, setFresh] = useState(() => new Set());
  // Switching org or account must not prune the previous book into the new key.
  if (slot.key !== key) setSlot({ key, book: readStore(key) });
  const book = slot.key === key ? slot.book : readStore(key);

  const commit = useCallback((next) => {
    setSlot({ key, book: next });
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ }
  }, [key]);

  useEffect(() => {
    const next = settleBook(book, events, ready);
    if (next !== book && JSON.stringify(next) !== JSON.stringify(book)) commit(next);
  }, [book, events, commit, ready]);

  const view = useMemo(() => applyBook(events, book), [events, book]);

  useEffect(() => {
    if (!open) {
      setFresh((prev) => (prev.size ? new Set() : prev));
      return;
    }
    const pending = view.listed.filter((ev) => !ev.read);
    if (!pending.length) return;
    setFresh((prev) => {
      const next = new Set(prev);
      for (const ev of pending) next.add(ev.id);
      return next;
    });
    commit(markRead(book, pending, events));
  }, [open, view, book, events, commit]);

  const clearOne = useCallback((ev) => {
    setFresh((prev) => {
      if (!prev.has(ev.id)) return prev;
      const next = new Set(prev);
      next.delete(ev.id);
      return next;
    });
    commit(markCleared(book, [ev], events));
  }, [book, events, commit]);

  const clearAll = useCallback(() => {
    setFresh(new Set());
    commit(markCleared(book, view.listed, events));
  }, [book, view, events, commit]);

  const listed = view.listed.map((ev) => (fresh.has(ev.id) ? { ...ev, read: false } : ev));
  return { listed, unread: open ? 0 : view.unread, clearOne, clearAll };
}

// The search field (system console-search): an inline combobox in the bar —
// type and the suggestions drop beneath it, ranked by utils/consoleSearch
// over modules, builder apps, workflows, workspaces, projects, agents,
// instances, the catalog's "how do I…" tasks, knowledge documents and the
// Help actions; the last row hands the words to NoVA. ⌘K focuses it; what
// this person opens is remembered per org and lifts the next ranking.
const searchKey = (uid, orgId) => `nv.search.${uid || 'local'}.${orgId}`;
const PRIOR = { module: 0.3, help: 0.25, app: 0.2, workflow: 0.1, instance: 0.1 };
const HINT_MAX = 90;
const trim = (s, n = HINT_MAX) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? `${t.slice(0, n - 1)}…` : t; };

function readHistory(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null') || {}; } catch { return {}; }
}

function useSearchItems(orgId) {
  const { docs: workspaces } = useEntityList('workspace', { org: orgId });
  const projects = useOrgProjects(orgId);
  const pipelines = useOrgPipelines(orgId);
  const { docs: agents } = useEntityList('agent', { org: orgId });
  const { docs: apps } = useEntityList('appInstance', { org: orgId });
  const { docs: builderApps } = useEntityList('app', { org: orgId });
  const { docs: knowledgeDocs } = useEntityList('knowledgeDoc', { org: orgId });

  return useMemo(() => {
    const base = `/orgs/${orgId}`;
    const list = [];
    for (const m of MODULES) {
      list.push({ id: `module:${m.to}`, group: 'Modules', label: m.label, aliases: [...(m.matches || []), m.to], hint: m.tag, keywords: m.purpose, to: `${base}/${m.to}`, prior: PRIOR.module });
      for (const t of m.tasks || []) {
        list.push({ id: `task:${m.to}:${t.ask}`, group: 'How do I…', label: t.ask, hint: trim(`${m.label} · ${t.steps || ''}`), keywords: t.steps, to: `${base}/${t.route || m.to}` });
      }
    }
    for (const a of (builderApps || []).filter((x) => !x.archived)) list.push({ id: `app:${a.id}`, group: 'Apps (builder)', label: a.name, hint: a.stage || 'define', keywords: a.goal, to: `${base}/builder?app=${a.id}`, updatedAt: a.updatedAt, prior: PRIOR.app });
    for (const p of pipelines || []) list.push({ id: `workflow:${p.id}`, group: 'Workflows', label: p.name, hint: p.wsName, keywords: p.description, to: `${base}/ws/${p.wsId}/pipelines/${p.id}`, updatedAt: p.updatedAt, prior: PRIOR.workflow });
    for (const w of workspaces || []) list.push({ id: `workspace:${w.id}`, group: 'Workspaces', label: w.name, hint: 'workspace', to: `${base}/ws/${w.id}`, updatedAt: w.updatedAt });
    for (const p of projects || []) list.push({ id: `project:${p.id}`, group: 'Projects', label: p.name, hint: p.wsName, to: `${base}/p/${p.wsId}/${p.id}`, updatedAt: p.updatedAt });
    for (const a of agents || []) list.push({ id: `agent:${a.id}`, group: 'Agents', label: a.name, hint: a.enabled ? 'enabled' : 'disabled', keywords: a.role || a.description, to: `${base}/workflows?tab=agents`, updatedAt: a.updatedAt });
    for (const a of apps || []) list.push({ id: `instance:${a.id}`, group: 'Instances', label: a.name, hint: a.urls?.app || a.status, to: `${base}/apps/${a.id}`, updatedAt: a.updatedAt, prior: PRIOR.instance });
    for (const d of knowledgeDocs || []) list.push({ id: `doc:${d.id}`, group: 'Docs', label: d.title || d.id, aliases: d.tags || [], hint: d.status || 'draft', keywords: String(d.content || '').slice(0, 200), to: `${base}/knowledge?doc=${d.id}`, updatedAt: d.updatedAt });
    list.push({ id: 'help:bug', group: 'Help', label: 'Report a bug', hint: 'screenshot + description to the nventr team', action: () => openBugReport(), prior: PRIOR.help });
    list.push({ id: 'help:bugs', group: 'Help', label: 'Your bug reports', hint: 'status of what you reported', to: '/bugs', prior: PRIOR.help });
    list.push({ id: 'help:feature', group: 'Help', label: 'Request a feature', hint: 'ask for something new — everyone can vote on it', action: () => openFeatureRequest(), prior: PRIOR.help });
    list.push({ id: 'help:features', group: 'Help', label: 'Feature board', hint: 'vote on requests, see what is planned and shipped', to: '/features', prior: PRIOR.help });
    return list;
  }, [orgId, workspaces, projects, pipelines, agents, apps, builderApps, knowledgeDocs]);
}

function SearchBox({ orgId, uid }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const key = searchKey(uid, orgId);
  const [history, setHistory] = useState(() => readHistory(key));
  useEffect(() => { setHistory(readHistory(key)); }, [key]);

  const items = useSearchItems(orgId);
  const index = useMemo(() => buildIndex(items), [items]);
  const hits = useMemo(() => {
    const ranked = rank(index, q, { history, limit: 12, perGroup: 4 });
    const words = q.trim();
    if (words) ranked.push({ item: { id: 'nova', group: 'Ask NoVA', label: `Ask NoVA: “${trim(words, 60)}”`, hint: 'anything not listed above', action: () => askNova(words) }, score: 0, ranges: [] });
    return ranked;
  }, [index, q, history]);

  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => {
    const row = listRef.current?.querySelector('.cp-suggest-row.is-active');
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [idx, hits]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const go = (hit) => {
    if (!hit) return;
    const { item } = hit;
    if (item.id !== 'nova') {
      const next = recordPick(history, item.id);
      setHistory(next);
      try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ }
    }
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
    if (item.action) item.action(); else navigate(item.to);
  };

  // Groups in order of their best hit; the rows a group gained in the
  // diversity fill-in sit with their group, not in a second block below.
  // `idx` walks the grouped order.
  const grouped = [];
  for (const h of hits) {
    const g = h.recent ? 'Recent' : h.item.group;
    const found = grouped.find((x) => x.group === g);
    if (found) found.hits.push(h); else grouped.push({ group: g, hits: [h] });
  }
  const flatHits = grouped.flatMap((g) => g.hits);
  let flat = -1;

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setIdx((i) => Math.min(flatHits.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { if (open) go(flatHits[idx]); }
    else if (e.key === 'Escape') {
      e.preventDefault();
      if (q) setQ(''); else { setOpen(false); inputRef.current?.blur(); }
    }
  };
  const listId = 'cp-suggest-list';
  const showList = open && hits.length > 0;

  return (
    <div className={`ch-search${open ? ' is-open' : ''}`} ref={rootRef} onClick={() => inputRef.current?.focus()} data-nova-note="Console search: type to jump to a module, app, workflow, document or a how-do-I task; Enter opens the highlighted row; the last row asks NoVA">
      <span className="ch-search-icon" aria-hidden="true">⌕</span>
      <input
        ref={inputRef}
        className="ch-search-input"
        type="text"
        role="combobox"
        aria-label="Search the console"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList ? `cp-suggest-${idx}` : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder="Search apps, workflows, modules…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <kbd>⌘K</kbd>
      {showList && (
        <div className="cp-suggest" id={listId} role="listbox" ref={listRef}>
          {grouped.map((g) => (
            <div key={g.group}>
              <div className="cp-suggest-group">{g.group}</div>
              {g.hits.map((h) => {
                flat += 1;
                const active = flat === idx;
                const mine = flat;
                return (
                  <div
                    key={h.item.id}
                    id={`cp-suggest-${mine}`}
                    role="option"
                    aria-selected={active}
                    className={`cp-suggest-row${active ? ' is-active' : ''}${h.item.id === 'nova' ? ' is-nova' : ''}`}
                    onMouseEnter={() => setIdx(mine)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); go(h); }}
                  >
                    <span className="cp-suggest-label">
                      {highlight(h.item.label, h.ranges).map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>))}
                    </span>
                    {h.item.hint && <span className="cs-help">{h.item.hint}</span>}
                  </div>
                );
              })}
            </div>
          ))}
          <div className="cp-suggest-foot"><kbd>↑</kbd><kbd>↓</kbd> move · <kbd>↵</kbd> open · <kbd>esc</kbd> close</div>
        </div>
      )}
    </div>
  );
}
